'use client';

import Popup from '@/components/Popup';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import { IconButton } from '@mui/material';
import { CloseOutlined } from '@zeelool/icon';
import { useLocale } from 'next-intl';
import { usePathname } from 'next/navigation';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { env } from '@/utils/env';

/** 写入 history.state，用于在历史栈占位；浏览器后退/前进触发 popstate 时优先关弹窗 */
const SHULEX_IFRAME_MODAL_HISTORY_KEY = '__shulexIframeModal' as const;

interface ShulexIframePopProps {
  show: boolean;
  iframeUrl: string;
  onClose: () => void;
}

interface ShulexChatIframeProps {
  url: string;
  loading: boolean;
  width?: string;
  height?: string | number;
  className?: string;
  title?: string;
}

const ShulexChatIframe = memo(
  ({
    url,
    loading,
    width = '100%',
    height,
    className = 'border-0',
    title = 'Shulex Customer Service',
  }: ShulexChatIframeProps) => {
    if (loading) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-gray-600">Loading...</span>
          </div>
        </div>
      );
    }

    if (!url) {
      return null;
    }

    return (
      <iframe
        src={url}
        width={width}
        height={height}
        className={className}
        title={title}
        loading="lazy"
      />
    );
  },
);

/** 只在一处维护 lang / email 的 query 序列化 */
function shulexChatQueryString(options: {
  lang: string;
  email: string | undefined | null;
  name?: string | undefined | null;
  customer_id?: string | undefined | null;
}): string {
  const p = new URLSearchParams();
  if (options.lang) {
    p.set('lang', options.lang);
  }
  if (options.email) {
    p.set('email', options.email);
  }
  if (options.name) {
    p.set('name', options.name);
  }
  if (options.customer_id) {
    p.set('customer_id', options.customer_id);
  }
  return p.toString();
}

/**
 * 接口下发的 iframe 基址上拼接 Shulex 所需 query：
 * - lang：next-intl 当前语言（与站点默认/用户语言选择一致）
 * - email：仅登录且有邮箱时拼接
 *
 * try：标准绝对 URL 用 URL 解析，可覆盖基址里已有的同名参数。
 * catch：无协议或非法地址时，仅把 query 拼到字符串末尾（与旧逻辑一致）。
 */
function buildShulexIframeSrc(
  base: string,
  options: {
    lang: string;
    email: string | undefined | null;
    name?: string | undefined | null;
    customer_id?: string | undefined | null;
  },
): string {
  if (!base) {
    return '';
  }
  const q = shulexChatQueryString({
    lang: options.lang,
    email: options.email,
    name: options.name,
    customer_id: options.customer_id,
  });
  if (!q) {
    return base;
  }
  try {
    const u = new URL(base);
    new URLSearchParams(q).forEach((value, key) => {
      u.searchParams.set(key, value);
    });
    return u.toString();
  } catch {
    return base.includes('?') ? `${base}&${q}` : `${base}?${q}`;
  }
}

/** next-intl 无 locale 时的兜底：与当前部署站点名对齐 */
function fallbackLangFromSite(): string {
  const site = (env.NEXT_PUBLIC_SITE_NAME || 'us').toLowerCase();
  const map: Record<string, string> = {
    us: 'en',
    de: 'de',
    fr: 'fr',
    ca: 'en',
    uk: 'en',
  };
  return map[site] ?? 'en';
}

/**
 * Shulex 客服 iframe 弹窗（与 InstadeskPop 视觉对齐、逻辑独立）
 */
export default function ShulexIframePop({ show, iframeUrl, onClose }: ShulexIframePopProps) {
  const t = useTranslations('layout.rightBottomFloating');
  const pathname = usePathname();
  const locale = useLocale();
  const isMobile = useStore((state) => state.isMobileScreen);
  const token = useStore((state) => state.token);
  const userInfo = useStore((state) => state.userInfo);

  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const pcPopupRef = useRef<HTMLDivElement>(null);
  const historyPushedRef = useRef(false);
  const showRef = useRef(show);
  const onCloseRef = useRef(onClose);
  showRef.current = show;
  onCloseRef.current = onClose;

  const builtUrl = useMemo(() => {
    if (!iframeUrl || !show) {
      return '';
    }
    const lang = (locale || fallbackLangFromSite()) as string;
    const email =
      token && userInfo?.email && String(userInfo.email).trim()
        ? String(userInfo.email).trim()
        : undefined;
    const name =
      token && userInfo?.name && String(userInfo.name).trim()
        ? String(userInfo.name).trim()
        : undefined;
    const customer_id = token && userInfo?.id ? String(userInfo.id) : undefined;
    return buildShulexIframeSrc(iframeUrl, {
      lang,
      email,
      name,
      customer_id,
    });
  }, [iframeUrl, show, locale, token, userInfo?.email, userInfo?.name, userInfo?.id]);

  useEffect(() => {
    if (!show) {
      setUrl('');
      setLoading(false);
    }
  }, [show]);

  useEffect(() => {
    if (show && builtUrl && builtUrl !== url) {
      setLoading(true);
      const timer = setTimeout(() => {
        setUrl(builtUrl);
        setLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [show, builtUrl, url]);

  /**
   * 仅移动端（全屏弹窗）：打开时 pushState，浏览器后退/前进先关弹窗而不离开视觉上的当前页。
   * PC 为右下角局部弹窗，不占用历史栈，路由前进/后退与地址栏照常切换，见下方 pathname 监听关窗。
   */
  useEffect(() => {
    if (!show || typeof window === 'undefined' || !isMobile) {
      return;
    }

    const prevRaw = window.history.state;
    const prevState =
      typeof prevRaw === 'object' && prevRaw !== null ? { ...(prevRaw as object) } : {};

    window.history.pushState(
      { ...prevState, [SHULEX_IFRAME_MODAL_HISTORY_KEY]: true },
      '',
      window.location.href,
    );
    historyPushedRef.current = true;

    const onPopState = () => {
      if (!historyPushedRef.current) {
        return;
      }
      historyPushedRef.current = false;
      if (showRef.current) {
        onCloseRef.current();
      }
    };

    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [show, isMobile]);

  /** PC 不托管历史占位；避免从移动端切到桌面后仍带着 push 标记误触发 history.back() */
  useEffect(() => {
    if (!isMobile) {
      historyPushedRef.current = false;
    }
  }, [isMobile]);

  const prevPathnameRef = useRef(pathname);

  /**
   * 路由 pathname 变化时关窗。
   * PC：与浏览器前进/后退、站内跳转一致，地址切换到新路由同时收起局部弹窗。
   * H5：全屏客服仍依赖此项在站内跳转时关窗。
   */
  useEffect(() => {
    const prev = prevPathnameRef.current;
    prevPathnameRef.current = pathname;
    if (!show || prev === pathname) {
      return;
    }
    historyPushedRef.current = false;
    onClose();
  }, [pathname, show, onClose]);

  useEffect(() => {
    if (show && !isMobile) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      const handleWheel = (e: WheelEvent) => {
        const target = e.target as Element;
        if (pcPopupRef.current?.contains(target)) {
          e.stopPropagation();
        }
      };

      document.addEventListener('wheel', handleWheel, { passive: true });

      return () => {
        document.body.style.overflow = originalOverflow;
        document.removeEventListener('wheel', handleWheel);
      };
    }
  }, [show, isMobile]);

  const handleClose = useCallback(() => {
    if (historyPushedRef.current && typeof window !== 'undefined') {
      historyPushedRef.current = false;
      window.history.back();
    }
    onClose();
  }, [onClose]);

  const PCContent = useCallback(
    () => (
      <div
        ref={pcPopupRef}
        className="fixed right-0 bottom-0 z-[3000] touch-none w-[722px] h-[639px] rounded-t-[12px] transition-transform duration-300 ease-in-out"
        style={{ transform: show ? 'translateY(0)' : 'translateY(105%)' }}
      >
        <div className="h-[50px] leading-[50px] text-[16px] text-white bg-primary-purple relative text-center font-bold rounded-t-[12px]">
          {t('serviceExperienceConsultant')}
          <IconButton
            aria-label={t('closeCustomerServiceChat')}
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
            size="small"
          >
            <CloseOutlined className="text-[24px] text-white" aria-hidden />
          </IconButton>
        </div>

        <div className="w-full h-[589px] bg-white relative">
          <ShulexChatIframe
            url={url}
            loading={loading}
            height="589"
            className="border-0 bg-white"
            title={t('serviceExperienceConsultant')}
          />
        </div>
      </div>
    ),
    [show, url, loading, t, handleClose],
  );

  return (
    <>
      <WebOnly>
        <PCContent />
      </WebOnly>

      <WapOnly>
        <Popup
          open={show}
          position="bottom"
          height="100%"
          onClose={handleClose}
          hideCloseIcon
          bodyClassName="!p-0 !max-h-[100vh]"
          round={false}
        >
          <div className="h-[50px] leading-[50px] text-base text-white bg-primary-purple relative text-center font-bold">
            {t('serviceExperienceConsultant')}
            <IconButton
              aria-label={t('closeCustomerServiceChat')}
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: '5.333vw',
                top: '50%',
                transform: 'translateY(-50%)',
              }}
              size="small"
            >
              <CloseOutlined className="text-[24px] text-white" aria-hidden />
            </IconButton>
          </div>

          <div className="h-[calc(100%-55px)] relative">
            <ShulexChatIframe
              url={url}
              loading={loading}
              height="100%"
              className="border-0"
              title={t('serviceExperienceConsultant')}
            />
          </div>
        </Popup>
      </WapOnly>
    </>
  );
}
